import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUser, ROLES, PERMISSIONS } from '../../contexts/UserContext';

/**
 * ProtectedRoute - Handles authentication and authorization for routes
 * 
 * @param {React.ReactNode} children - Components to render when access is granted
 * @param {Array} roles - User roles that can access this route
 * @param {Array} permissions - Permissions required to access this route
 * @param {boolean} requireAll - If true, user must have ALL permissions; if false, ANY permission is sufficient
 * @param {string} redirectTo - Where to redirect unauthenticated users
 * @param {React.ReactNode} fallback - Component to show during loading state
 * @param {boolean} rememberRedirect - Remember where user was trying to go after login
 * @param {Function} onAccessDenied - Callback for when access is denied (useful for logging)
 */
const ProtectedRoute = ({ 
  children, 
  roles = [], 
  permissions = [], 
  requireAll = false,
  redirectTo = '/login',
  fallback = null,
  rememberRedirect = true,
  onAccessDenied = null
}) => {
  const { 
    currentUser, 
    loading, 
    isAuthenticated,
    hasAnyRole,
    hasAnyPermission, 
    hasAllPermissions,
    error
  } = useUser();
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // Save intended destination if rememberRedirect is enabled
  useEffect(() => {
    if (!isAuthenticated && rememberRedirect) {
      sessionStorage.setItem('redirectAfterLogin', JSON.stringify({
        pathname: location.pathname,
        search: location.search
      }));
    }
  }, [isAuthenticated, rememberRedirect, location]);
  
  // Memoize route decisions for better performance
  const routeDecision = useMemo(() => {
    // Show error state if user context has an error
    if (error) {
      console.error('Authentication error:', error);
      return {
        type: 'ERROR',
        message: error.message || 'Authentication error'
      };
    }
    
    // Show loading state
    if (loading) {
      return { type: 'LOADING' };
    }

    // Check if user is authenticated
    if (!isAuthenticated || !currentUser) {
      return { type: 'REDIRECT', to: redirectTo };
    }

    // Check role-based access
    if (roles.length > 0) {
      const hasRequiredRole = hasAnyRole(roles);
      
      if (!hasRequiredRole) {
        // Determine the appropriate dashboard based on user's actual role
        const roleRedirects = {
          'SUPER_ADMIN': '/super-admin-dashboard',
          'ADMIN': '/admin-dashboard',
          'MANAGER': '/manager-dashboard',
          'FINANCE_MANAGER': '/finance-dashboard',
          'CONTENT_MANAGER': '/content-moderation',
          'AGGREGATOR': '/aggregator-dashboard',
          'CHANNEL_OWNER': '/channel-dashboard',
          'CREATOR': '/creator-dashboard',
          'CONTENT_CREATOR': '/creator-dashboard',
          'STUDIO': '/studio-dashboard',
          'INVESTOR': '/investor-dashboard',
          'PREMIUM_INVESTOR': '/investor-dashboard',
          'SUPPORT_AGENT': '/support-dashboard',
          'ANALYTICS_VIEWER': '/analytics-dashboard',
          'USER': '/dashboard'
        };

        const userDashboard = roleRedirects[currentUser.role] || '/dashboard';
        
        // Call onAccessDenied callback if provided
        if (onAccessDenied) {
          onAccessDenied({
            type: 'INSUFFICIENT_ROLE',
            requiredRoles: roles,
            userRole: currentUser.role,
            userId: currentUser.id,
            path: location.pathname
          });
        }
        
        return { type: 'REDIRECT', to: userDashboard };
      }
    }

    // Check permission-based access
    if (permissions.length > 0) {
      const hasRequiredPermissions = requireAll 
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
      
      if (!hasRequiredPermissions) {
        // Call onAccessDenied callback if provided
        if (onAccessDenied) {
          onAccessDenied({
            type: 'INSUFFICIENT_PERMISSIONS',
            requiredPermissions: permissions,
            userPermissions: currentUser.permissions,
            userId: currentUser.id,
            path: location.pathname
          });
        }
        
        return { type: 'REDIRECT', to: '/unauthorized' };
      }
    }

    // User has access
    return { type: 'RENDER' };
  }, [
    currentUser, 
    loading, 
    isAuthenticated, 
    roles, 
    permissions, 
    requireAll,
    redirectTo,
    hasAnyRole, 
    hasAnyPermission, 
    hasAllPermissions,
    error,
    location.pathname,
    onAccessDenied
  ]);

  // Handle route decision
  switch (routeDecision.type) {
    case 'LOADING':
      return fallback || (
        <div className="loading-container" data-testid="protected-route-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      );
      
    case 'ERROR':
      return (
        <div className="error-container" data-testid="protected-route-error">
          <div className="error-icon">⚠️</div>
          <h3>Authentication Error</h3>
          <p>{routeDecision.message}</p>
          <button onClick={() => navigate(0)}>Retry</button>
        </div>
      );
      
    case 'REDIRECT':
      return <Navigate to={routeDecision.to} state={{ from: location }} replace />;
      
    case 'RENDER':
    default:
      return children;
  }
};

// Specialized Protected Routes for common use cases

export const AdminRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.TENANT_ADMIN, ROLES.ADMIN]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const ManagerRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const ContentModeratorRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CONTENT_MANAGER]} 
    permissions={[PERMISSIONS.APPROVE_CONTENT, PERMISSIONS.REJECT_CONTENT]}
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const FinanceRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.FINANCE_MANAGER]} 
    permissions={[PERMISSIONS.VIEW_FINANCE]}
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const AggregatorRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.AGGREGATOR]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const ChannelOwnerRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.CHANNEL_OWNER]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const CreatorRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.CREATOR, ROLES.CONTENT_CREATOR]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const InvestorRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.INVESTOR, ROLES.PREMIUM_INVESTOR]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const StudioRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.STUDIO]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const SupportRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SUPPORT_AGENT]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const AnalyticsRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYTICS_VIEWER]} 
    permissions={[PERMISSIONS.VIEW_ANALYTICS]}
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

// New specialized routes for common combinations

export const SuperAdminOnlyRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN]} 
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export const DashboardRoute = ({ children, fallback, onAccessDenied }) => (
  <ProtectedRoute 
    roles={Object.values(ROLES)} // Any role can access
    fallback={fallback}
    onAccessDenied={onAccessDenied}
  >
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;